import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, message, Modal, Typography } from 'antd';
import {
  ExclamationCircleOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import {
  ErpDataGrid,
  ErpMasterShell,
  useErpMasterQuery
} from '../../../../components/common/erp-grid';
import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { buildContraColumns } from './contraVoucherColumns';
import { contraVoucherApi } from './contraVoucherApi';
import { friendlyErrorFor } from './contraVoucherHelpers';
import ContraVoucherModal from './ContraVoucherModal';
// Stage 6B.9F — period guard for Post action.
import { accountsApi } from '../accountsApi';
import {
  classifyPeriodForDate, periodGuardError, loadPeriodsForGuard
} from '../accountingPeriodGuard';
import '../accounts.css';

const { Title } = Typography;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Contra Voucher page (Stage 6B.9C).
//
// Grid + Add/Edit/View modal + right-click context menu (View / Edit
// Draft / Post / Cancel / Delete Draft). Approval-MFA is wired for
// Post + Cancel + Delete — Contra is the first module to gate the
// soft-delete of a draft with MFA (see Stage 6B.9A §8 hardening
// item #1).
//
// Modal sequencing matches the Journal Voucher page: confirm Modal
// resolves on afterClose, then runWithApproval opens the MFA modal
// cleanly — two modals never stack visually.
// ─────────────────────────────────────────────────────────────────

const ADAPTER = { list: (query) => contraVoucherApi.list(query) };

function defaultOnError(err) {
  message.error({ content: friendlyErrorFor(err), key: 'contra-list' });
}

export default function ContraVoucherPage() {
  const { gridHandlers, refetch, query } = useErpMasterQuery({
    adapter: ADAPTER, pageSize: 25, onError: defaultOnError
  });
  const currentPage = query && query.page     ? query.page     : 1;
  const pageSize    = query && query.pageSize ? query.pageSize : 25;

  // Ledger lookup cache — shared between grid columns + modal so the
  // From / To account cells render account names immediately. The
  // modal also writes to it as the operator searches.
  const ledgerLookupRef = useRef(new Map());
  const columns = useMemo(
    () => buildContraColumns({ currentPage, pageSize, ledgerLookupRef }),
    [currentPage, pageSize]);

  // Seed the lookup cache at first page render so the grid's account
  // cells aren't all "…" on initial load.
  useEffect(() => {
    contraVoucherApi.cashBankLookup('', 200).then((rows) => {
      for (const r of rows) ledgerLookupRef.current.set(r.id, r);
    }).catch(() => {});
  }, []);

  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [modalRecord, setModalRecord] = useState(null);

  // Stage 6B.9F — period guard fetch for Post action.
  const [periods, setPeriods] = useState([]);
  useEffect(() => {
    let cancelled = false;
    loadPeriodsForGuard(accountsApi).then((out) => {
      if (cancelled) return;
      setPeriods((out && out.rows) || []);
    });
    return () => { cancelled = true; };
  }, []);

  const openAdd = useCallback(() => {
    setModalMode('add'); setModalRecord(null); setModalOpen(true);
  }, []);

  const openWithFetch = useCallback(async (record, modeKind) => {
    try {
      const full = await contraVoucherApi.get(record.id);
      setModalMode(modeKind);
      setModalRecord(full);
      setModalOpen(true);
    } catch (e) {
      message.error(friendlyErrorFor(e));
    }
  }, []);
  const openEdit = useCallback((r) => openWithFetch(r, 'edit'), [openWithFetch]);
  const openView = useCallback((r) => openWithFetch(r, 'view'), [openWithFetch]);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const onSaved = useCallback(async () => {
    setModalOpen(false);
    await refetch();
  }, [refetch]);

  // ── POST flow ─────────────────────────────────────────────
  const handlePost = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft contras can be posted. This contra is already ${record.status}.`);
      return;
    }
    // Stage 6B.9F — period guard (client-side pre-check).
    const cls = classifyPeriodForDate(periods, record.voucherDate);
    const guard = periodGuardError(cls);
    if (guard) {
      message.error(guard.message);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Post contra ${record.voucherNo}?`,
        icon: <CheckCircleOutlined style={{ color: '#3f8600' }} />,
        content: 'Posting will update ledger balances and lock the voucher. You will be asked for a 6-digit Approval MFA code to confirm.',
        okText: 'Post',
        cancelText: 'Cancel',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.contra.post',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Post contra voucher',
        resourceLabel:  `${record.voucherNo}`,
        action: (approvalMfaChallengeId) =>
          contraVoucherApi.post(record.id, { approvalMfaChallengeId })
      });
      message.success('Contra posted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(friendlyErrorFor(err));
    }
  }, [refetch, runWithApproval, periods]);

  // ── CANCEL flow ───────────────────────────────────────────
  const handleCancel = useCallback(async (record) => {
    if (record.status !== 'posted') {
      message.warning(`Only posted contras can be cancelled (this one is ${record.status}).`);
      return;
    }
    const reason = await new Promise((resolve) => {
      let value = '';
      let okClicked = false;
      const ReasonForm = () => (
        <div>
          <p style={{ marginTop: 0 }}>
            Cancelling will REVERSE the ledger impact of this posted contra.
            Provide a short reason (audit trail).
          </p>
          <Form layout="vertical">
            <Form.Item label="Reason" required>
              <Input.TextArea rows={3} maxLength={500}
                onChange={(e) => { value = e.target.value; }} />
            </Form.Item>
          </Form>
        </div>
      );
      Modal.confirm({
        title: `Cancel contra ${record.voucherNo}?`,
        icon: <ExclamationCircleOutlined />,
        content: <ReasonForm />,
        okText: 'Continue',
        okButtonProps: { danger: true },
        cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked ? value : null)
      });
    });
    if (!reason) return;
    if (String(reason).trim().length < 3) {
      message.error('Reason must be at least 3 characters.');
      return;
    }
    try {
      await runWithApproval({
        operationKey:   'accounts.contra.cancel',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Cancel contra voucher',
        resourceLabel:  `${record.voucherNo}`,
        action: (approvalMfaChallengeId) =>
          contraVoucherApi.cancel(record.id, { reason: String(reason).trim() }, { approvalMfaChallengeId })
      });
      message.success('Contra cancelled.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(friendlyErrorFor(err));
    }
  }, [refetch, runWithApproval]);

  // ── DELETE flow ───────────────────────────────────────────
  // Stage 6B.9C — Contra is the first module to L1-gate the soft-
  // delete of a draft. Use the same Approval-MFA flow as post/cancel.
  const handleDelete = useCallback(async (record) => {
    if (record.status !== 'draft') {
      message.warning(`Only draft contras can be deleted (this one is ${record.status}). Cancel posted vouchers instead.`);
      return;
    }
    const confirmed = await new Promise((resolve) => {
      let okClicked = false;
      Modal.confirm({
        title: `Delete draft ${record.voucherNo}?`,
        icon:  <ExclamationCircleOutlined />,
        content: 'The draft will be soft-deleted. You will be asked for a 6-digit Approval MFA code to confirm.',
        okText: 'Delete', okButtonProps: { danger: true }, cancelText: 'Keep',
        onOk:    () => { okClicked = true; },
        onCancel: () => { okClicked = false; },
        afterClose: () => resolve(okClicked)
      });
    });
    if (!confirmed) return;
    try {
      await runWithApproval({
        operationKey:   'accounts.contra.delete',
        resourceType:   'accounting_voucher',
        resourceId:     record.id,
        operationLabel: 'Delete draft contra voucher',
        resourceLabel:  `${record.voucherNo}`,
        action: (approvalMfaChallengeId) =>
          contraVoucherApi.remove(record.id, { approvalMfaChallengeId })
      });
      message.success('Draft deleted.');
      await refetch();
    } catch (err) {
      if (err && err.name === 'ApprovalMfaCancelled') return;
      message.error(friendlyErrorFor(err));
    }
  }, [refetch, runWithApproval]);

  const actionsProp     = useMemo(() => ({ add: true, view: true, edit: true, delete: false }), []);
  const pageSizeOptions = useMemo(() => ['15', '25', '50', '100'], []);

  const contextMenuActions = useMemo(() => ([
    { key: 'view',   label: 'View',         icon: <EyeOutlined />,            onClick: openView },
    { key: 'edit',   label: 'Edit Draft',   icon: <EditOutlined />,           onClick: openEdit },
    { key: 'post',   label: 'Post',         icon: <CheckCircleOutlined />,    onClick: handlePost },
    { key: 'cancel', label: 'Cancel',       icon: <CloseCircleOutlined />,    danger: true, onClick: handleCancel },
    { key: 'delete', label: 'Delete Draft', icon: <DeleteOutlined />,         danger: true, onClick: handleDelete }
  ]), [openView, openEdit, handlePost, handleCancel, handleDelete]);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <ErpDataGrid
          title="Contra Vouchers"
          subtitle="Cash and bank transfers (cash↔bank, bank↔bank, cash↔cash)"
          gridId="accounts-contras-v1"
          columns={columns}
          rowKey="id"
          rowHeight="compact"
          stickyHeader
          selectable
          actionWidth={56}
          pageSizeOptions={pageSizeOptions}
          searchable
          searchPlaceholder="Search by voucher no / narration…"
          searchShortcut
          exportable
          exportFilename="contra-vouchers"
          actions={actionsProp}
          addButtonText="New Contra Voucher"
          emptyTitle="No contra vouchers yet"
          emptyText="Use Contra to record cash deposits, withdrawals, and bank-to-bank transfers."
          emptyCtaLabel="New Contra Voucher"
          onAdd={openAdd}
          onEdit={openEdit}
          onView={openView}
          onRowClick={openView}
          contextMenuActions={contextMenuActions}
          {...gridHandlers}
        />
        <ContraVoucherModal
          open={modalOpen}
          mode={modalMode}
          record={modalRecord}
          onClose={closeModal}
          onSaved={onSaved}
        />
        {ApprovalMfaModalSlot}
      </div>
    </ErpMasterShell>
  );
}
